/**
 * TablesWithJoinsCard — renders a comma-separated list of table-reference
 * segments, each with optional JOIN strips. Shared by `UpdateRenderer`'s
 * "Tables" (multi-table MySQL UPDATE) and "From" (PG / MSSQL UPDATE…FROM)
 * sections. Each segment ships a disabled `×`; the section ships disabled
 * `[+ add table]` + `[+ add join]` per § 1.4.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { AddButton } from '../common/AddButton';
import { RemoveButton } from '../common/RemoveButton';
import { vqeCopy } from '../../ux/vqeCopy';
import { ClauseCard } from '../select/ClauseCard';
import { extractFromSegmentJoins } from '../select/extractFromJoins';
import { JoinLine } from '../select/JoinLine';
import { splitTopLevelCommas } from '../select/splitTopLevel';

export interface TablesWithJoinsCardProps {
  readonly bufferText: string;
  readonly start: number;
  readonly end: number;
  readonly options: ParseOptions;
  readonly title: string;
  readonly testIdPrefix: string;
  /**
   * Fallback label rendered as the JoinLine's "left anchor" when a segment
   * has no leading text — used by the multi-table UPDATE form to show the
   * primary target as the JOIN's left-hand side.
   */
  readonly fallbackLeadingText?: string;
}

export function TablesWithJoinsCard({
  bufferText,
  start,
  end,
  options,
  title,
  testIdPrefix,
  fallbackLeadingText = '',
}: TablesWithJoinsCardProps) {
  const segments = splitTopLevelCommas(bufferText, start, end, options).map((item) => ({
    rangeStart: item.start,
    rangeEnd: item.end,
    rawText: item.text,
    split: extractFromSegmentJoins(bufferText, item.start, item.end, options),
  }));
  return (
    <ClauseCard
      title={title}
      testId={testIdPrefix}
      footer={
        <div className="flex flex-wrap gap-2">
          <AddButton label={vqeCopy.actions.addJoin} testId={`${testIdPrefix}-add-join`} />
        </div>
      }
    >
      <ul className="flex flex-col gap-2" data-testid={`${testIdPrefix}-items`}>
        {segments.map((seg, idx) => {
          const leadingText = seg.split.leading.text || seg.rawText;
          return (
            <li
              key={`${seg.rangeStart}-${idx}`}
              className="flex flex-col gap-1.5"
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              {leadingText.length > 0 ? (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1">
                  <span
                    className="flex-1 truncate font-mono text-xs"
                    data-testid={`${testIdPrefix}-item-text-${idx}`}
                  >
                    {leadingText}
                  </span>
                  <RemoveButton
                    testId={`${testIdPrefix}-item-remove-${idx}`}
                    ariaLabel="Remove table"
                  />
                </div>
              ) : null}
              {seg.split.joins.length > 0 ? (
                <ul
                  className="m-0 flex list-none flex-col gap-1.5 pl-3 p-0"
                  data-testid={`${testIdPrefix}-item-${idx}-joins`}
                >
                  {seg.split.joins.map((join, jIdx) => (
                    <li key={`${join.start}-${jIdx}`}>
                      <JoinLine
                        bufferText={bufferText}
                        join={join}
                        options={options}
                        leadingText={leadingText.length > 0 ? leadingText : fallbackLeadingText}
                        testIdPrefix={`${testIdPrefix}-item-${idx}-join-${jIdx}`}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </ClauseCard>
  );
}
